import { React, useState, useEffect, useRef } from 'react'
import { Box, Typography, Icon } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import AdxInput from 'components/custom/AdxInput'
import { InputAdornment } from '@mui/material'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postBankData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object({
  epf_status: Yup.number().nullable().required('EPF Contribution Status is required'),
  epf_category: Yup.object()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: Yup.object().nullable().required('EPF Category is required'),
      otherwise: Yup.object().nullable(),
    }),
  epf_acc_number: Yup.string()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: Yup.string()
        .nullable()
        .required('EPF Account Number is required')
        .matches(/^[0-9\b]+$/, 'EPF Account Number can only contain numbers'),
      otherwise: Yup.string().nullable(),
    }),
  epf_employee_contribution_rate: Yup.number()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: Yup.number().nullable().required('Employee EPF Rate is required'),
      otherwise: Yup.number().nullable(),
    }),
  employee_custom_contribution_rate: Yup.string()
    .nullable()
    .when('epf_employee_contribution_rate', {
      is: 2,
      then: Yup.string()
        .nullable()
        .required('Custom Rate is required')
        .matches(/^(0?[0-9]|1[0123456789])$/, 'Custom Rate must not be more than 19%'),
      otherwise: Yup.string().nullable(),
    }),
  epf_employer_contribution_rate: Yup.number()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: Yup.number().nullable().required('Employer EPF Rate is required'),
      otherwise: Yup.number().nullable(),
    }),
  employer_custom_contribution_rate: Yup.string()
    .nullable()
    .when('epf_employer_contribution_rate', {
      is: 1,
      then: Yup.string()
        .nullable()
        .required('Custom Rate is required')
        .matches(/^(0?[0-9]|1[0123456789])$/, 'Custom Rate must not be more than 19%'),
      otherwise: Yup.string().nullable(),
    }),

  // NEW FIELD – but behaviour same as previously added
  socso_worker_type: Yup.number()
    .nullable()
    .required('SOCSO Worker Type is required'),

  socso_status: Yup.number().nullable().required('SOCSO Contribution Status is required'),
  socso_category: Yup.object()
    .nullable()
    .when('socso_status', {
      is: 1,
      then: Yup.object().nullable().required('SOCSO Category is required'),
      otherwise: Yup.object().nullable(),
    }),
  socso_acc_number: Yup.string()
    .nullable()
    .when('socso_status', {
      is: 1,
      then: Yup.string()
        .nullable()
        .required('SOCSO Account Number is required')
        .matches(/^[a-zA-Z0-9-]+$/, 'SOCSO Account Number can only contain alphanumeric'),
      otherwise: Yup.string().nullable(),
    }),
  skbbk_deduction: Yup.number().nullable().required('SKBBK Contribution Status is required'),
  eis_status: Yup.number().nullable().required('EIS Contribution Status is required'),
  hrdf_status: Yup.number().nullable().required('Employee HRDF status is required'),
  zakat_status: Yup.number().nullable().required('Zakat Contribution Status is required'),
  zakat_acc_number: Yup.string()
    .nullable()
    .when('zakat_status', {
      is: 1,
      then: Yup.string()
        .nullable()
        .required('Zakat Account Number is required')
        .matches(/^[0-9\b]+$/, 'Zakat Account Number can only contain numbers'),
      otherwise: Yup.string().nullable(),
    }),
  zakat_amount: Yup.string()
    .nullable()
    .when('zakat_status', {
      is: 1,
      then: Yup.string()
        .nullable()
        .required('Zakat Amount is required')
        .matches(/^[0-9]{1,11}(?:\.[0-9]{1,3})?$/, 'Zakat Amount must be valid'),
      otherwise: Yup.string().nullable(),
    }),
})

const EPFCategory = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'Malaysian/Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 3,
    category: 'Category 3',
    description:
      'Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 4,
    category: 'Category 4',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age 60 years old and above',
  },
  {
    id: 5,
    category: 'Category 5',
    description: 'Malaysians AND age 60 years old and above',
  },
]

const EPFEmployeeOneTwo = [
  { id: 0, epf_rate: '9%' },
  { id: 1, epf_rate: '11%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployeeThreeFour = [
  { id: 0, epf_rate: '5.5%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployeeFive = [
  { id: 0, epf_rate: '0%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployerRate = [
  { id: 0, epf_rate: 'Fixed' },
  { id: 1, epf_rate: 'Custom' },
]

const SOCSO_CATEGORY_LOCAL = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'For employees who are less than 60 years of age, except for those who have attained 55 years of age and have no prior contributions before they reach 55',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'For employees who have reached the age of 60 OR for eligible new employees who are 55 years of age',
  },
]

const SOCSO_CATEGORY_FOREIGN = [
  {
    id: 1,
    category:
      'First Category Contribution (Employment Injury Scheme and Invalidity Scheme)',
    description:
      'For foreign workers who first enter or contribute for the first time under the PERKESO Invalidity Scheme under the age of 55 years old.',
  },
  {
    id: 2,
    category:
      'Second Category Contribution (Employment Injury Scheme – LINDUNG PEKERJA)',
    description:
      'For foreign workers who have reached 55 years old or older when they first enter the PERKESO Invalidity Scheme; OR foreign workers who have reached 60 years old and are still working.',
  },
]


const SOCSOWorkerTypeOptions = [
  { id: 1, label: 'Local employee' },
  { id: 2, label: 'Foreign worker' },
]

const StatutoryBodyDetails = ({
  data,
  employeeID,
  windowCheckEmployeeFile,
  company_hrdf_status,
  company_skbbk_enabled,
  company_skbbk_deduct_from_salary,
}) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [disable, setDisable] = useState(true)

  const [initialValues, setInitialValues] = useState({
    epf_status: '',
    epf_category: '',
    epf_acc_number: '',
    epf_employee_contribution_rate: '',
    employee_custom_contribution_rate: '',
    epf_employer_contribution_rate: '',
    employer_custom_contribution_rate: '',
    socso_worker_type: 1,
    socso_status: '',
    socso_category: '',
    socso_acc_number: '',
    skbbk_deduction: '',
    eis_status: '',
    hrdf_status: '',
    zakat_status: '',
    zakat_acc_number: '',
    zakat_amount: '',
  })

  const mutation = useMutation((value) => postBankData(employeeID, value), {
    onSuccess: () => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeeBankData', employeeID])
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitStatutoryBody = (values) => {
    const newValues = {
      ...values,
      epf_category: values.epf_category ? values.epf_category.id : null,
      socso_category: values.socso_category ? values.socso_category.id : null,
      skbbk_deduction: values.socso_status === 1 ? values.skbbk_deduction : 0,
    }
    mutation.mutate(newValues)
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  useEffect(() => {
    if (data) {


      const rawWorkerType = data?.data?.socso_worker_type ?? 1
      const resolvedEisStatus =
        rawWorkerType === 2 ? 0 : data?.data?.eis_status

      const socsoOptionsForWorker =
        rawWorkerType === 2 ? SOCSO_CATEGORY_FOREIGN : SOCSO_CATEGORY_LOCAL

      const initialSocsoCategory = data?.data?.socso_category
        ? socsoOptionsForWorker.find((item) => item.id === data?.data?.socso_category)
        : null
      const companyAllowsSkbbk = company_skbbk_enabled === 1
      const companyDeductsSkbbk = company_skbbk_deduct_from_salary === 1
      const initialSkbbkDeduction =
        data?.data?.socso_status !== 1
          ? 0
          : data?.data?.skbbk_deduction ?? (companyAllowsSkbbk && companyDeductsSkbbk ? 1 : 0)

      setInitialValues({
        epf_status: data?.data?.epf_status,
        epf_category: data?.data?.epf_category
          ? EPFCategory.find((item) => item.id === data?.data?.epf_category)
          : { id: 0, category: 'No Category' },
        epf_acc_number: data?.data?.epf_acc_number || '',
        epf_employee_contribution_rate: data?.data?.epf_employee_contribution_rate,
        employee_custom_contribution_rate:
          data?.data?.employee_custom_contribution_rate || 0,
        epf_employer_contribution_rate: data?.data?.epf_employer_contribution_rate,
        employer_custom_contribution_rate:
          data?.data?.employer_custom_contribution_rate || 0,
        socso_worker_type: rawWorkerType,
        socso_status: data?.data?.socso_status,
        socso_category: initialSocsoCategory,
        socso_acc_number: data?.data?.socso_acc_number || '',
        skbbk_deduction: initialSkbbkDeduction,
        eis_status: resolvedEisStatus,
        hrdf_status: data?.data?.employee_hrdf_status,
        zakat_status: data?.data?.zakat_status,
        zakat_acc_number: data?.data?.zakat_acc_number,
        zakat_amount: data?.data?.zakat_amount || 0,
      })
    }
  }, [
    data,
    company_skbbk_enabled,
    company_skbbk_deduct_from_salary,
  ])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitStatutoryBody}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
        resetForm,
      }) => {
        const companyAllowsSkbbk = company_skbbk_enabled === 1
        const companyDeductsSkbbk = company_skbbk_deduct_from_salary === 1
        const employeeContributesSocso = values.socso_status === 1
        const skbbkCanEdit = employeeContributesSocso
        const skbbkFieldDisabled = disable || !skbbkCanEdit

        return (
        <Form>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              alignItems: 'center',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Typography color="light-gray-text.main" fontWeight={500}>
              Statutory body details
            </Typography>
          </Box>

          {/* Form Body */}
          <Box
            p={2}
            borderBottom={windowCheckEmployeeFile && '1px solid'}
            sx={{
              borderColor: 'gray-line.main',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* EPF status */}
            <Box>
              <AdxDropdown
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                label="EPF contribution status"
                disabled={disable}
                size="standard"
                value={values.epf_status}
                name="epf_status"
                onChange={(event) => {
                  if (values.epf_status === 1) {
                    if (event.target.value !== 1) {
                      setFieldValue('epf_category', { id: 0, category: 'No Category' })
                      setFieldValue('epf_acc_number', null)
                      setFieldValue('epf_employee_contribution_rate', 0)
                      setFieldValue('employee_custom_contribution_rate', 0)
                      setFieldValue('epf_employer_contribution_rate', 0)
                      setFieldValue('employer_custom_contribution_rate', 0)
                    }
                  } else {
                    if (event.target.value === 1) {
                      setFieldValue('epf_category', null)
                      setFieldValue('epf_acc_number', null)
                      setFieldValue('epf_employee_contribution_rate', null)
                      setFieldValue('employee_custom_contribution_rate', 0)
                      setFieldValue('epf_employer_contribution_rate', null)
                      setFieldValue('employer_custom_contribution_rate', 0)
                    } else {
                      setFieldValue('epf_category', { id: 0, category: 'No Category' })
                      setFieldValue('epf_acc_number', null)
                      setFieldValue('epf_employee_contribution_rate', 0)
                      setFieldValue('employee_custom_contribution_rate', 0)
                      setFieldValue('epf_employer_contribution_rate', 0)
                      setFieldValue('employer_custom_contribution_rate', 0)
                    }
                  }
                  handleChange(event)
                }}
                sx={{ width: 1 }}
                errorMessage={<ErrorMessage name="epf_status" component={AdxErrorMessage} />}
                isErrorShown={touched.epf_status && errors.epf_status}
              >
                <AdxMenuItem value={1} selectedvalue={values.epf_status}>
                  Yes
                </AdxMenuItem>
                <AdxMenuItem value={0} selectedvalue={values.epf_status}>
                  No
                </AdxMenuItem>
              </AdxDropdown>
            </Box>

            {/* EPF category */}
            <Box gap={2} display="flex" alignItems="center">
              <AdxDropdown
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                label="EPF contribution category"
                disabled={disable ? true : values.epf_status === 1 ? false : true}
                size="standard"
                placeholder="EPF contribution category"
                value={values.epf_category}
                name="epf_category"
                sx={{ width: '100%' }}
                onChange={(tre) => {
                  if (values?.epf_category?.id === 1 || values?.epf_category?.id === 2) {
                    if (tre.target.value.id === 1 || tre.target.value.id === 2) {
                      handleChange(tre)
                    } else {
                      handleChange(tre)
                      setFieldValue('epf_employee_contribution_rate', null)
                    }
                  } else {
                    if (tre.target.value.id === 1 || tre.target.value.id === 2) {
                      handleChange(tre)
                      setFieldValue('epf_employee_contribution_rate', null)
                    } else {
                      handleChange(tre)
                    }
                  }
                }}
                renderValue={(value) => (
                  <Box>
                    <Typography>{value.category}</Typography>
                  </Box>
                )}
                errorMessage={<ErrorMessage name="epf_category" component={AdxErrorMessage} />}
                isErrorShown={touched.epf_category && errors.epf_category}
              >
                {EPFCategory.map((item) => (
                  <AdxMenuItem key={item.id} value={item} selectedvalue={values.epf_category}>
                    <Box sx={{ maxWidth: 525, display: 'flex', flexDirection: 'column' }}>
                      <Typography color="dark-text.main">{item.category}</Typography>
                      <Typography color="light-gray-text.main" sx={{ whiteSpace: 'wrap' }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </AdxMenuItem>
                ))}
              </AdxDropdown>
            </Box>

            {/* EPF account */}
            <Box>
              <AdxInput
                label="EPF account no."
                disabled={disable ? true : values.epf_status === 1 ? false : true}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="EPF account no."
                value={values.epf_acc_number}
                name="epf_acc_number"
                onChange={handleChange}
                sx={{ width: '100%' }}
                errorMessage={<ErrorMessage name="epf_acc_number" component={AdxErrorMessage} />}
                isErrorShown={touched.epf_acc_number && errors.epf_acc_number}
              />
            </Box>

            {/* Employee EPF rate */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box width={1}>
                <AdxDropdown
                  label="Employee EPF Rate"
                  disabled={disable ? true : values.epf_status === 1 ? false : true}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Employee EPF Rate"
                  value={values.epf_employee_contribution_rate}
                  name="epf_employee_contribution_rate"
                  onChange={(event) => {
                    setFieldValue('employee_custom_contribution_rate', 0)
                    handleChange(event)
                  }}
                  errorMessage={
                    <ErrorMessage
                      name="epf_employee_contribution_rate"
                      component={AdxErrorMessage}
                    />
                  }
                  isErrorShown={
                    touched.epf_employee_contribution_rate &&
                    errors.epf_employee_contribution_rate
                  }
                >
                  {values?.epf_category?.id === 1 || values?.epf_category?.id === 2
                    ? EPFEmployeeOneTwo.map((rate) => (
                      <AdxMenuItem
                        key={rate.id}
                        value={rate.id}
                        selectedvalue={values.epf_employee_contribution_rate}
                      >
                        {rate.epf_rate}
                      </AdxMenuItem>
                    ))
                    : values?.epf_category?.id === 3 || values?.epf_category?.id === 4
                      ? EPFEmployeeThreeFour.map((rate) => (
                        <AdxMenuItem
                          key={rate.id}
                          value={rate.id}
                          selectedvalue={values.epf_employee_contribution_rate}
                        >
                          {rate.epf_rate}
                        </AdxMenuItem>
                      ))
                      : EPFEmployeeFive.map((rate) => (
                        <AdxMenuItem
                          key={rate.id}
                          value={rate.id}
                          selectedvalue={values.epf_employee_contribution_rate}
                        >
                          {rate.epf_rate}
                        </AdxMenuItem>
                      ))}
                </AdxDropdown>
              </Box>
              <Box width={{ xs: '100%', sm: '20%' }}>
                {values.epf_employee_contribution_rate === 2 && (
                  <AdxInput
                    label="Custom rate"
                    disabled={disable ? true : values.epf_status === 1 ? false : true}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Custom rate"
                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    value={values.employee_custom_contribution_rate}
                    name="employee_custom_contribution_rate"
                    onChange={handleChange}
                    errorMessage={
                      <ErrorMessage
                        name="employee_custom_contribution_rate"
                        component={AdxErrorMessage}
                      />
                    }
                    isErrorShown={
                      touched.employee_custom_contribution_rate &&
                      errors.employee_custom_contribution_rate
                    }
                  />
                )}
              </Box>
            </Box>

            {/* Employer EPF rate */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box width={1}>
                <AdxDropdown
                  label="Employer EPF Rate"
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  disabled={disable ? true : values.epf_status === 1 ? false : true}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Employer EPF Rate"
                  name="epf_employer_contribution_rate"
                  value={values.epf_employer_contribution_rate}
                  onChange={(event) => {
                    setFieldValue('employer_custom_contribution_rate', 0)
                    handleChange(event)
                  }}
                  errorMessage={
                    <ErrorMessage
                      name="epf_employer_contribution_rate"
                      component={AdxErrorMessage}
                    />
                  }
                  isErrorShown={
                    touched.epf_employer_contribution_rate &&
                    errors.epf_employer_contribution_rate
                  }
                >
                  {EPFEmployerRate.map((rate) => (
                    <AdxMenuItem
                      key={rate.id}
                      value={rate.id}
                      selectedvalue={values.epf_employer_contribution_rate}
                    >
                      {rate.epf_rate}
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>
              <Box width={{ xs: '100%', sm: '20%' }}>
                {values.epf_employer_contribution_rate === 1 && (
                  <AdxInput
                    label="Custom rate"
                    disabled={disable ? true : values.epf_status === 1 ? false : true}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Custom rate"
                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    value={values.employer_custom_contribution_rate}
                    name="employer_custom_contribution_rate"
                    onChange={handleChange}
                    errorMessage={
                      <ErrorMessage
                        name="employer_custom_contribution_rate"
                        component={AdxErrorMessage}
                      />
                    }
                    isErrorShown={
                      touched.employer_custom_contribution_rate &&
                      errors.employer_custom_contribution_rate
                    }
                  />
                )}
              </Box>
            </Box>

            {/* SOCSO contribution status */}
            <Box width={1}>
              <AdxDropdown
                label="SOCSO contribution status"
                disabled={disable}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="SOCSO contribution status"
                value={values.socso_status}
                name="socso_status"
                onChange={(event) => {
                  const nextStatus = event.target.value

                  if (values.socso_status === 1 && nextStatus !== 1) {
                    // Turning SOCSO OFF → clear related fields + EIS
                    setFieldValue('socso_category', null)
                    setFieldValue('socso_acc_number', null)
                    setFieldValue('skbbk_deduction', 0)
                    setFieldValue('eis_status', 0)
                  }

                  if (nextStatus === 1) {
                    // Turning SOCSO ON → set a sensible default category if empty
                    const workerType = values.socso_worker_type ?? 1
                    const options =
                      workerType === 2 ? SOCSO_CATEGORY_FOREIGN : SOCSO_CATEGORY_LOCAL

                    if (!values.socso_category) {
                      setFieldValue('socso_category', options[0] || null)
                    }
                    if (companyAllowsSkbbk && companyDeductsSkbbk) {
                      setFieldValue(
                        'skbbk_deduction',
                        data?.data?.skbbk_deduction ?? 1
                      )
                    } else {
                      setFieldValue('skbbk_deduction', data?.data?.skbbk_deduction ?? 0)
                    }
                  }

                  handleChange(event)
                }}
                errorMessage={<ErrorMessage name="socso_status" component={AdxErrorMessage} />}
                isErrorShown={touched.socso_status && errors.socso_status}
              >
                <AdxMenuItem value={1} selectedvalue={values.socso_status}>
                  Yes
                </AdxMenuItem>
                <AdxMenuItem value={0} selectedvalue={values.socso_status}>
                  No
                </AdxMenuItem>
              </AdxDropdown>
            </Box>

            {/* SOCSO worker type */}
            <Box width={1}>

              <AdxDropdown
                label="SOCSO worker type"

                disabled={disable || values.socso_status === 0}
                sxLabel={{
                  color:
                    disable || values.socso_status === 0
                      ? 'light-gray-text.main'
                      : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="SOCSO worker type"
                value={values.socso_worker_type ?? 1}
                name="socso_worker_type"
                onChange={(event) => {
                  const newType = Number(event.target.value)

                  handleChange(event)

                  // Update EIS based on worker type (foreign → 0)
                  if (newType === 2) {
                    setFieldValue('eis_status', 0)
                  } else if (newType === 1 && values.eis_status === 0) {
                    // if switch back to local, give a default EIS=1 (HR can still change)
                    setFieldValue('eis_status', 1)
                  }

                  // Adjust SOCSO category options list + default
                  const options =
                    newType === 2 ? SOCSO_CATEGORY_FOREIGN : SOCSO_CATEGORY_LOCAL

                  if (!values.socso_category || !options.some((o) => o.id === values.socso_category.id)) {
                    // if current category doesn’t exist in new list, set default Category 1
                    setFieldValue('socso_category', options[0] || null)
                  }
                }}
                errorMessage={
                  <ErrorMessage name="socso_worker_type" component={AdxErrorMessage} />
                }
                isErrorShown={touched.socso_worker_type && errors.socso_worker_type}
              >
                {SOCSOWorkerTypeOptions.map((opt) => (
                  <AdxMenuItem
                    key={opt.id}
                    value={opt.id}
                    selectedvalue={values.socso_worker_type ?? 1}
                  >
                    {opt.label}
                  </AdxMenuItem>
                ))}
              </AdxDropdown>
            </Box>

            {/* SOCSO category */}
            <Box display="flex" alignItems="center">
              <Box width="100%">
                <AdxDropdown
                  label="SOCSO contribution category"
                  // HR can choose Category 1 or 2 for both local & foreign,
                  // only locked when SOCSO = No or whole form is disabled
                  disabled={disable || values.socso_status !== 1}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="SOCSO contribution category"
                  value={values.socso_category}
                  name="socso_category"
                  onChange={handleChange}
                  renderValue={(value) => (
                    <Box>
                      <Typography>{value?.category}</Typography>
                    </Box>
                  )}
                  errorMessage={<ErrorMessage name="socso_category" component={AdxErrorMessage} />}
                  isErrorShown={touched.socso_category && errors.socso_category}
                >
                  {(values.socso_worker_type === 2
                    ? SOCSO_CATEGORY_FOREIGN
                    : SOCSO_CATEGORY_LOCAL
                  ).map((item) => (
                    <AdxMenuItem
                      key={item.id}
                      value={item}
                      selectedvalue={values.socso_category}
                    >
                      <Box sx={{ maxWidth: 525, whiteSpace: 'wrap' }}>
                        <Typography color="dark-text.main" pb={0.375}>
                          {item.category}
                        </Typography>
                        <Typography color="light-gray-text.main">
                          {item.description}
                        </Typography>
                      </Box>
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>
            </Box>

            {/* SOCSO account */}
            <Box width="100%">
              <AdxInput
                label="SOCSO account no."
                disabled={disable ? true : values.socso_status === 1 ? false : true}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="SOCSO account no."
                value={values.socso_acc_number}
                name="socso_acc_number"
                onChange={handleChange}
                errorMessage={<ErrorMessage name="socso_acc_number" component={AdxErrorMessage} />}
                isErrorShown={touched.socso_acc_number && errors.socso_acc_number}
              />
            </Box>

            {/* SKBBK status */}
            <Box width={1}>
              <AdxDropdown
                label="SKBBK contribution status"
                disabled={skbbkFieldDisabled}
                sxLabel={{
                  color: skbbkFieldDisabled ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="SKBBK contribution status"
                value={values.skbbk_deduction}
                name="skbbk_deduction"
                onChange={handleChange}
                errorMessage={<ErrorMessage name="skbbk_deduction" component={AdxErrorMessage} />}
                isErrorShown={touched.skbbk_deduction && errors.skbbk_deduction}
              >
                <AdxMenuItem value={1} selectedvalue={values.skbbk_deduction}>
                  Yes
                </AdxMenuItem>
                <AdxMenuItem value={0} selectedvalue={values.skbbk_deduction}>
                  No
                </AdxMenuItem>
              </AdxDropdown>
            </Box>

            {/* EIS status + note */}
            <Box width={1}>
              <AdxDropdown
                label="EIS contribution status"
                disabled={disable || values.socso_worker_type === 2} // lock for foreign workers
                sxLabel={{
                  color:
                    disable || values.socso_worker_type === 2
                      ? 'light-gray-text.main'
                      : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="EIS contribution status"
                value={values.eis_status}
                name="eis_status"
                onChange={handleChange}
                errorMessage={<ErrorMessage name="eis_status" component={AdxErrorMessage} />}
                isErrorShown={touched.eis_status && errors.eis_status}
              >
                <AdxMenuItem value={1} selectedvalue={values.eis_status}>
                  Yes
                </AdxMenuItem>
                <AdxMenuItem value={0} selectedvalue={values.eis_status}>
                  No
                </AdxMenuItem>
              </AdxDropdown>

              {values.socso_worker_type === 2 && !disable && (
                <Typography variant="caption" color="light-gray-text.main" sx={{ mt: 0.5 }}>
                  Foreign workers are not covered by EIS. EIS is for Malaysian citizens only.
                </Typography>
              )}
            </Box>

            {/* HRDF (if company HRDF enabled) */}
            {company_hrdf_status === 1 && (
              <Box width={1}>
                <AdxDropdown
                  label="HRDF levy status"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="HRDF levy status"
                  value={values.hrdf_status}
                  name="hrdf_status"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="hrdf_status" component={AdxErrorMessage} />}
                  isErrorShown={touched.hrdf_status && errors.hrdf_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.hrdf_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.hrdf_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>
            )}

            {/* Zakat status */}
            <Box width={1}>
              <AdxDropdown
                label="Zakat contribution status"
                disabled={disable}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="Zakat contribution status"
                value={values.zakat_status}
                name="zakat_status"
                onChange={(event) => {
                  if (values.zakat_status === 1 && event.target.value !== 1) {
                    setFieldValue('zakat_acc_number', null)
                  }
                  setFieldValue('zakat_amount', 0)
                  handleChange(event)
                }}
                errorMessage={<ErrorMessage name="zakat_status" component={AdxErrorMessage} />}
                isErrorShown={touched.zakat_status && errors.zakat_status}
              >
                <AdxMenuItem value={1} selectedvalue={values.zakat_status}>
                  Yes
                </AdxMenuItem>
                <AdxMenuItem value={0} selectedvalue={values.zakat_status}>
                  No
                </AdxMenuItem>
              </AdxDropdown>
            </Box>

            {/* Zakat details */}
            {values.zakat_status === 1 && (
              <>
                <Box width="100%">
                  <AdxInput
                    label="Zakat Account Number"
                    disabled={disable}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Zakat Account Number"
                    value={values.zakat_acc_number}
                    name="zakat_acc_number"
                    onChange={handleChange}
                    sx={{ mb: 2.5 }}
                    errorMessage={
                      <ErrorMessage name="zakat_acc_number" component={AdxErrorMessage} />
                    }
                    isErrorShown={touched.zakat_acc_number && errors.zakat_acc_number}
                  />
                </Box>
                <Box
                  width={{ xs: '100%', sm: '60%' }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <AdxInput
                    label="Zakat amount"
                    disabled={disable}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="XX,XXX"
                    value={values.zakat_amount}
                    name="zakat_amount"
                    onChange={handleChange}
                    startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                    sx={{ width: { xs: '70%', sm: '50%' }, mr: 1 }}
                    errorMessage={
                      <ErrorMessage name="zakat_amount" component={AdxErrorMessage} />
                    }
                    isErrorShown={touched.zakat_amount && errors.zakat_amount}
                  />
                  <Typography sx={{ pt: 3 }} variant="body1" color="light-gray-text.main">
                    (per month)
                  </Typography>
                </Box>
              </>
            )}

            {/* Save / Cancel buttons (only when windowCheckEmployeeFile AND editing) */}
            {windowCheckEmployeeFile && !disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  columnGap: 1,
                }}
              >
                <AdxButton
                  variant="outlined"
                  onClick={() => cancelEditForm(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                <AdxButton
                  variant="contained"
                  onClick={() => handleCheckRef(handleSubmit)}
                  sx={{ width: 0.2 }}
                >
                  Save
                </AdxButton>
              </Box>
            )}
          </Box>

          {/* Ghost "Edit statutory body details" button – only when viewing (disabled) */}
          {windowCheckEmployeeFile && disable && (
            <Box sx={{ p: 0.5 }}>
              <AdxButton
                variant="ghost"
                sx={{
                  pl: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onClick={() => setDisable(false)}
              >
                <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                  Edit statutory body details
                </Typography>
              </AdxButton>
            </Box>
          )}
        </Form>
        )
      }}
    </Formik>
  )
}

export default StatutoryBodyDetails
